import { useState } from "react";

/**
 * Numbered coordinate overlay for the LOCKED report-card-template.png.
 * DO NOT alter the PNG. Boxes are pure CSS overlays for visual approval.
 * Rendered as a content pane inside AdminLayout (sidebar remains visible).
 *
 * Template intrinsic size: 1545 x 1999 px.
 */

const IMG_W = 1545;
const IMG_H = 1999;

const FIELDS = [
  {
    id: 1, name: "Pet Name", color: "#B3362D",
    x: 100, y: 340, w: 780, h: 220,
    note: "Rendered inside the red ribbon banner",
  },
  {
    id: 2, name: "Pet Photo", color: "#3E731C",
    x: 830, y: 160, w: 590, h: 465,
    note: "Auto-cropped/scaled to fit the pink taped frame",
  },
  { id: 3,  name: "Subject 1", color: "#203A6B", x: 210, y: 792,  w: 460, h: 113 },
  { id: 4,  name: "Subject 2", color: "#203A6B", x: 210, y: 905,  w: 460, h: 117 },
  { id: 5,  name: "Subject 3", color: "#203A6B", x: 210, y: 1022, w: 460, h: 116 },
  { id: 6,  name: "Subject 4", color: "#203A6B", x: 210, y: 1138, w: 460, h: 117 },
  { id: 7,  name: "Subject 5", color: "#203A6B", x: 210, y: 1255, w: 460, h: 116 },
  { id: 8,  name: "Subject 6", color: "#203A6B", x: 210, y: 1371, w: 460, h: 116 },
  { id: 9,  name: "Subject 7", color: "#203A6B", x: 210, y: 1487, w: 460, h: 119 },
  { id: 10, name: "Subject 8", color: "#203A6B", x: 210, y: 1606, w: 460, h: 125 },
  { id: 11, name: "Grade 1", color: "#5A972D", x: 683, y: 792,  w: 230, h: 113 },
  { id: 12, name: "Grade 2", color: "#5A972D", x: 683, y: 905,  w: 230, h: 117 },
  { id: 13, name: "Grade 3", color: "#5A972D", x: 683, y: 1022, w: 230, h: 116 },
  { id: 14, name: "Grade 4", color: "#5A972D", x: 683, y: 1138, w: 230, h: 117 },
  { id: 15, name: "Grade 5", color: "#5A972D", x: 683, y: 1255, w: 230, h: 116 },
  { id: 16, name: "Grade 6", color: "#5A972D", x: 683, y: 1371, w: 230, h: 116 },
  { id: 17, name: "Grade 7", color: "#5A972D", x: 683, y: 1487, w: 230, h: 119 },
  { id: 18, name: "Grade 8", color: "#5A972D", x: 683, y: 1606, w: 230, h: 125 },
  // Four teacher comment fields — each sits above its dotted underline
  // (detected line centers: y = 994, 1176, 1358, 1570)
  { id: 19, name: "Comment 1", color: "#C88A0F", x: 930, y: 900,  w: 540, h: 100, note: "Writes above dotted line 1 (y≈994)" },
  { id: 20, name: "Comment 2", color: "#C88A0F", x: 930, y: 1082, w: 540, h: 100, note: "Writes above dotted line 2 (y≈1176)" },
  { id: 21, name: "Comment 3", color: "#C88A0F", x: 930, y: 1264, w: 540, h: 100, note: "Writes above dotted line 3 (y≈1358)" },
  { id: 22, name: "Comment 4", color: "#C88A0F", x: 930, y: 1476, w: 540, h: 100, note: "Writes above dotted line 4 (y≈1570)" },
  // Report number is intentionally STATIC for the initial release — the baked-in
  // "LIMITED EDITION NO. 20260123" is used for every report. Dynamic numbering
  // will be added in a later version. No dynamic field is drawn there.
];

const pct = (v, total) => `${(v / total) * 100}%`;

export default function TemplateOverlay() {
  const [showBoxes, setShowBoxes] = useState(true);
  const [showLabels, setShowLabels] = useState(true);

  return (
    <main
      style={{
        padding: "26px 30px",
        fontFamily: "'Nunito', sans-serif",
        color: "#2A2540",
      }}
      data-testid="template-overlay-page"
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontSize: 30, fontWeight: 800, margin: 0 }}>Template Overlay</h1>
          <p style={{ color: "#8A8699", margin: "4px 0 0", fontSize: 14.5 }}>
            Locked source: <code style={{ background: "#EEE9FB", padding: "1px 6px", borderRadius: 4, fontSize: 12.5 }}>/templates/report-card-template.png</code> ·
            intrinsic {IMG_W}×{IMG_H}px · boxes are CSS overlays, the PNG is untouched.
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14, fontSize: 14, fontWeight: 600, color: "#6E6880" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={showBoxes}
              onChange={(e) => setShowBoxes(e.target.checked)}
              data-testid="toggle-boxes"
            /> Boxes
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={showLabels}
              onChange={(e) => setShowLabels(e.target.checked)}
              data-testid="toggle-labels"
            /> Labels
          </label>
        </div>
      </div>

      {/* Overlay + Legend */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 320px", gap: 22, marginTop: 22, alignItems: "start" }}>
        <div
          style={{
            position: "relative",
            width: "100%",
            maxWidth: 780,
            aspectRatio: `${IMG_W} / ${IMG_H}`,
            background: "#fff",
            border: "1px solid #ECEAF3",
            borderRadius: 12,
            overflow: "hidden",
            boxShadow: "0 8px 24px rgba(30, 20, 60, 0.08)",
          }}
          data-testid="overlay-canvas"
        >
          <img
            src="/templates/report-card-template.png"
            alt="Locked report card template"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", userSelect: "none", display: "block" }}
            draggable={false}
            data-testid="locked-template-image"
          />
          {showBoxes && FIELDS.map((f) => (
            <div
              key={f.id}
              style={{
                position: "absolute",
                left: pct(f.x, IMG_W), top: pct(f.y, IMG_H),
                width: pct(f.w, IMG_W), height: pct(f.h, IMG_H),
                border: `2px solid ${f.color}`, background: `${f.color}22`,
                boxSizing: "border-box",
                display: "flex", alignItems: "flex-start", justifyContent: "flex-start",
              }}
              data-testid={`field-box-${f.id}`}
            >
              {showLabels && (
                <span style={{ background: f.color, color: "#fff", padding: "2px 6px", fontSize: 10, fontWeight: 800, lineHeight: 1, borderBottomRightRadius: 4 }}>
                  {f.id}. {f.name}
                </span>
              )}
            </div>
          ))}
        </div>

        <aside style={{ background: "#fff", border: "1px solid #ECEAF3", borderRadius: 12, padding: "18px 18px 16px" }}>
          <h2 style={{ fontSize: 17, fontWeight: 800, margin: "0 0 12px" }}>Field Legend (22 total)</h2>
          <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 9 }}>
            {FIELDS.map((f) => (
              <li key={f.id} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span style={{
                  background: f.color, color: "#fff",
                  width: 22, height: 22, borderRadius: 5,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: 800, flexShrink: 0,
                }}>
                  {f.id}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13.5, color: "#2A2540" }}>{f.name}</div>
                  <div style={{ fontSize: 11.5, color: "#8A8699" }}>
                    x:{f.x} y:{f.y} · {f.w}×{f.h}px{f.note ? ` · ${f.note}` : ""}
                  </div>
                </div>
              </li>
            ))}
          </ol>
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #EFEDF5", fontSize: 11.5, color: "#9A93B0" }}>
            Intrinsic template {IMG_W}×{IMG_H}px (≈182 DPI at 8.5×11″). Boxes use percentages, so shifting them is a one-line edit in <code style={{ background: "#F5F3FB", padding: "1px 4px", borderRadius: 3 }}>TemplateOverlay.jsx → FIELDS[]</code>.
          </div>
        </aside>
      </div>
      <div style={{ height: 32 }} />
    </main>
  );
}
